import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const vendorEmpty = { name: "", contact_person: "", phone: "", email: "", address: "", gstin: "" };

export default function Purchase() {
    const { activeHotelId } = useAuth();
    const [vendors, setVendors] = useState([]);
    const [orders, setOrders] = useState([]);
    const [openVendor, setOpenVendor] = useState(false);
    const [vendorForm, setVendorForm] = useState(vendorEmpty);
    const [openPO, setOpenPO] = useState(false);
    const [poForm, setPoForm] = useState({ vendor_id: "", vendor_name: "", items: [], expected_date: "", notes: "" });
    const [newItem, setNewItem] = useState({ name: "", quantity: 1, unit: "pcs", price: 0 });

    const load = async () => {
        if (!activeHotelId) return;
        const [v, o] = await Promise.all([
            api.get(`/purchase/vendors?hotel_id=${activeHotelId}`),
            api.get(`/purchase/orders?hotel_id=${activeHotelId}`),
        ]);
        setVendors(v.data); setOrders(o.data);
    };
    useEffect(() => { load(); }, [activeHotelId]);

    const submitVendor = async () => {
        try {
            await api.post("/purchase/vendors", { ...vendorForm, hotel_id: activeHotelId });
            toast.success("Vendor added"); setOpenVendor(false); setVendorForm(vendorEmpty); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const submitPO = async () => {
        if (!poForm.items.length) return;
        try {
            const vendor = vendors.find(v => v.id === poForm.vendor_id);
            await api.post("/purchase/orders", { ...poForm, hotel_id: activeHotelId, vendor_name: vendor?.name || "" });
            toast.success("Purchase order created");
            setOpenPO(false); setPoForm({ vendor_id: "", vendor_name: "", items: [], expected_date: "", notes: "" });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const addPOItem = () => {
        if (!newItem.name) return;
        setPoForm({ ...poForm, items: [...poForm.items, { ...newItem, quantity: Number(newItem.quantity), price: Number(newItem.price) }] });
        setNewItem({ name: "", quantity: 1, unit: "pcs", price: 0 });
    };

    const updatePO = async (id, status) => {
        try { await api.patch(`/purchase/orders/${id}`, { status }); toast.success("Updated"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Purchase" subtitle={`${vendors.length} vendors · ${orders.length} orders`}>
            <Tabs defaultValue="orders">
                <TabsList>
                    <TabsTrigger value="orders" data-testid="tab-po">Purchase Orders</TabsTrigger>
                    <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
                </TabsList>

                <TabsContent value="orders" className="mt-4">
                    <div className="flex justify-end mb-3">
                        <Dialog open={openPO} onOpenChange={setOpenPO}>
                            <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="po-add-btn"><Plus className="w-4 h-4 mr-1" /> New PO</Button></DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div><Label className="text-xs uppercase tracking-wider">Vendor</Label>
                                        <Select value={poForm.vendor_id} onValueChange={v => setPoForm({ ...poForm, vendor_id: v })}>
                                            <SelectTrigger data-testid="po-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                                            <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs uppercase tracking-wider">Expected Date</Label>
                                        <Input type="date" value={poForm.expected_date} onChange={e => setPoForm({ ...poForm, expected_date: e.target.value })} /></div>
                                    <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Line Items</div>
                                        {poForm.items.map((it, idx) => (
                                            <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-100">
                                                <span>{it.quantity} {it.unit} · {it.name}</span>
                                                <span className="font-mono">₹{(it.quantity * it.price).toFixed(2)}</span>
                                            </div>
                                        ))}
                                        <div className="grid grid-cols-5 gap-2 mt-2">
                                            <Input placeholder="Name" value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} className="col-span-2" data-testid="po-item-name" />
                                            <Input type="number" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem({ ...newItem, quantity: e.target.value })} />
                                            <Input type="number" placeholder="Price" value={newItem.price} onChange={e => setNewItem({ ...newItem, price: e.target.value })} />
                                            <Button onClick={addPOItem} variant="outline" size="sm" data-testid="po-item-add">Add</Button>
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter><Button onClick={submitPO} disabled={!poForm.vendor_id || !poForm.items.length} className="bg-[#0A1C3E]" data-testid="po-submit">Create PO</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>

                    <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                                <tr><th className="px-4 py-3">PO #</th><th className="px-4 py-3">Vendor</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
                            </thead>
                            <tbody>
                                {orders.map(o => (
                                    <tr key={o.id} className="border-b border-slate-100" data-testid={`po-row-${o.id}`}>
                                        <td className="px-4 py-3 font-mono text-sm">{o.po_number}</td>
                                        <td className="px-4 py-3 text-sm">{o.vendor_name}</td>
                                        <td className="px-4 py-3 text-sm">{o.items?.length}</td>
                                        <td className="px-4 py-3 text-sm font-mono font-semibold">₹{Number(o.total).toLocaleString()}</td>
                                        <td className="px-4 py-3"><span className="text-xs uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{o.status}</span></td>
                                        <td className="px-4 py-3 text-right">
                                            {o.status === "draft" && <Button size="sm" variant="outline" onClick={() => updatePO(o.id, "sent")} className="h-7 text-xs">Send</Button>}
                                            {o.status === "sent" && <Button size="sm" variant="outline" onClick={() => updatePO(o.id, "received")} className="h-7 text-xs">Receive</Button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="vendors" className="mt-4">
                    <div className="flex justify-end mb-3">
                        <Dialog open={openVendor} onOpenChange={setOpenVendor}>
                            <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="vendor-add-btn"><Plus className="w-4 h-4 mr-1" /> Vendor</Button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>New Vendor</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    {[["name", "Name"], ["contact_person", "Contact Person"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"], ["gstin", "GSTIN"]].map(([k, l]) => (
                                        <div key={k}><Label className="text-xs uppercase tracking-wider">{l}</Label>
                                            <Input value={vendorForm[k]} onChange={e => setVendorForm({ ...vendorForm, [k]: e.target.value })} data-testid={`vendor-${k}`} /></div>
                                    ))}
                                </div>
                                <DialogFooter><Button onClick={submitVendor} disabled={!vendorForm.name} className="bg-[#0A1C3E]" data-testid="vendor-submit">Create</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {vendors.map(v => (
                            <Card key={v.id} className="p-4 border border-slate-200 rounded-md shadow-none">
                                <div className="font-semibold text-slate-900">{v.name}</div>
                                <div className="text-xs text-slate-500 mt-1">{v.contact_person}</div>
                                <div className="text-xs text-slate-500 mt-2">{v.phone} · {v.email}</div>
                                {v.gstin && <div className="text-xs font-mono text-slate-600 mt-2">GSTIN: {v.gstin}</div>}
                            </Card>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </AppShell>
    );
}
